import { AgGridModule } from 'ag-grid-angular';
import { CommonModule, CurrencyPipe, DatePipe, DecimalPipe, TitleCasePipe } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { AccountInfoModule } from '../account-info-header/account-info.module';
import { AuthorizeEffects } from '../authorize/effects/authorize.effects';
import { CreateRequestEffects } from '../create-request/effects/create-request.effects';
import { NamePipe } from '../shared/pipes/NamePipe';
import { BaseRestService } from '../shared/services/BaseRestService';
import { SharedModule } from '../shared/shared.module';
import { TransactionViewModule } from '../transaction-view/transaction-view.module';
import { AchContributionReasonComponent } from './components/ach-contribution-reason/ach-contribution-reason.component';
import { AchExtraPromptsComponent } from './components/ach-extra-prompts/ach-extra-prompts.component';
import { AchSourceOfFundsComponent } from './components/ach-source-of-funds/ach-source-of-funds.component';
import { AchTransferDirectionComponent } from './components/ach-transfer-direction/ach-transfer-direction.component';
import { CheckAddressDetailsComponent } from './components/check/check-address-details/check-address-details.component';
import { CheckDeliveryMethodComponent } from './components/check/check-delivery-method/check-delivery-method.component';
import { CheckExtraPromptsComponent } from './components/check/check-extra-prompts/check-extra-prompts.component';
import { CheckMemoComponent } from './components/check/check-memo/check-memo.component';
import { CheckPayeeNameComponent } from './components/check/check-payee-name/check-payee-name.component';
import { CheckPayeeTypeComponent } from './components/check/check-payee-type/check-payee-type.component';
import { CheckReasonComponent } from './components/check/check-reason/check-reason.component';
import { CheckRecipientComponent } from './components/check/check-recipient/check-recipient.component';
import { CheckThirdPartyAddressFieldsComponent } from './components/check/check-third-party-address-fields/check-third-party-address-fields.component';
import { CheckThirdPartyDomesticFieldsComponent } from './components/check/check-third-party-domestic-fields/check-third-party-domestic-fields.component';
import { CheckThirdPartyInternationalFieldsComponent } from './components/check/check-third-party-international-fields/check-third-party-international-fields.component';
import { TransactionDetailsCheckComponent } from './components/check/transaction-details-check/transaction-details-check.component';
import { ConfirmationPageComponent } from './components/confirmation-page/confirmation-page.component';
import { InputPageComponent } from './components/input-page/input-page.component';
import { ItExtraPromptsComponent } from './components/it-extra-prompts/it-extra-prompts.component';
import { PositionsLotsTableComponent } from './components/positions-lots-table/positions-lots-table.component';
import { PositionsTableMultipleToAccountComponent } from './components/positions-table-multiple-to-account/positions-table-multiple-to-account.component';
import { PositionsTableMultipleReviewComponent } from './components/positions-table-multiple-review/positions-table-multiple-review.component';
import { TransactionDetailsAchComponent } from './components/transaction-details-ach/transaction-details-ach.component';
import { TransactionDetailsItComponent } from './components/transaction-details-it/transaction-details-it.component';
import { MultipleTransactionDetailsItComponent } from './components/multiple-transaction-details-it/multiple-transaction-details-it.component';
import { TransactionDetailsWireInterimIsoComponent } from './components/transaction-details-wire-interim-iso/transaction-details-wire-interim-iso.component';
import { TransactionFormComponent } from './components/transaction-form/transaction-form.component';
import { TransactionsTableComponent } from './components/transactions-table/transactions-table.component';
import { TrustCommentComponent } from './components/trust-comment/trust-comment.component';
import { TrustDepositCodeComponent } from './components/trust-deposit-code/trust-deposit-code.component';
import { TrustIncomePrincipalComponent } from './components/trust-income-principal/trust-income-principal.component';
import { TrustPaidForComponent } from './components/trust-paid-for/trust-paid-for.component';
import { TrustTaxCategoryComponent } from './components/trust-tax-category/trust-tax-category.component';
import { TrustTaxCodeComponent } from './components/trust-tax-code/trust-tax-code.component';
import { WireAbaComponent } from './components/wire-aba/wire-aba.component';
import { WireAccountNumberComponent } from './components/wire-account-number/wire-account-number.component';
import { WireDateDropdownControlComponent } from './components/wire-date-dropdown-control/wire-date-dropdown-control.component';
import { WireExtraPromptsComponent } from './components/wire-extra-prompts/wire-extra-prompts.component';
import { WireReceivingAccountHolderComponent } from './components/wire-receiving-account-holder/wire-receiving-account-holder.component';
import { WireIsoReceivingAccountHolderComponent } from './components/wire-iso-receiving-account-holder/wire-iso-receiving-account-holder.component';
import { WireRecipientComponent } from './components/wire-recipient/wire-recipient.component';
import { WireRequestingAccountHolderComponent } from './components/wire-requesting-account-holder/wire-requesting-account-holder.component';
import { WireStandingAuthComponent } from './components/wire-standing-auth/wire-standing-auth.component';
import { WireTransmissionNotesComponent } from './components/wire-transmission-notes/wire-transmission-notes.component';
import { CreateRequestComponent } from './container/create-request.component';
import { TrustFieldsComponent } from './container/trust-fields/trust-fields.component';
import { AchEffects } from './effects/ach.effects';
import { CashieringDateEffects } from './effects/cashiering-date.effects';
import { CheckEffects } from './effects/check.effects';
import { InternalTransferEffects } from './effects/internal-transfer.effects';
import { InternalTransfersEffects } from './effects/internal-transfer-list.effects';
import { LetterOfAuthorizationEffects } from './effects/letter-of-authorization.effects';
import { PtrsEffects } from './effects/ptrs.effects';
import { StandingAuthorizationsNicknameEffects } from './effects/standing-authorizations-nickname.effects';
import { StandingAuthorizationsEffects } from './effects/standing-authorizations.effects';
import { WireEffects } from './effects/wire.effects';
import * as fromCreateRequest from './reducers';
import { TransactionTableEffects } from './effects/transaction-table.effects';
import { OptionsComponent } from './components/options/options.component';
import { TemplatesOptionComponent } from './components/templates-option/templates-option.component';
import { WireTemplatesComponent } from './components/wire-templates/wire-templates.component';
import { SubmitAndCreateOptionComponent } from './components/submit-and-create-option/submit-and-create-option.component';
import { DraftsEffects } from './effects/drafts.effects';
import { SaveAsTemplateModalComponent } from './components/save-as-template-modal/save-as-template-modal.component';
import { CheckModifiedPayeeComponent } from './components/check/check-modified-payee/check-modified-payee.component';
import { WireModifiedPayeeComponent } from './components/wire-modified-payee/wire-modified-payee.component'
import { ItMultipleCashInputsComponent } from './components/it-multiple-cash-inputs/it-multiple-cash-inputs.component';
import { ItMultipleTaxWithholdingsComponent } from './components/it-multiple-tax-withholdings/it-multiple-tax-withholdings.component';
import { ItMultipleTrustFieldsComponent } from './components/it-multiple-trust-fields/it-multiple-trust-fields.component';
import { MultipleToAccountComponent } from './components/it-multiple-to-account/it-multiple-to-account.component';
import { WireRecipientInterimComponent } from './components/wire-recipient-interim/wire-recipient-interim.component';
import { WireCreditToInterimComponent } from './components/wire-credit-to-interim/wire-credit-to-interim.component';
import { WireIntermediaryIsoComponent } from './components/wire-intermediary-iso/wire-intermediary-iso.component';
import { ReviewPageComponent } from './components/review-page/review-page.component';
import { SwitchModule } from '@charlesschwab/as-ui-core';
import { WireIsoFourStepComponent } from './components/wire-iso-four-step-intermediary/wire-iso-four-step-intermediary.component';
import { RecurringTransactionsTableComponent } from './components/recurring-transactions-table/recurring-transactions-table.component';
import { MasterStatusViewComponent } from './components/master-status-view/master-status-view.component';
import { MasterTransactionsTableComponent } from './components/master-transaction-table/master-transactions-table.component';
import { MasterTransactionTableEffects } from './effects/master-transaction-table.effects';
import { MasterStatusViewEffects } from './effects/master-status-view.effects';
import { ReviewInputPositionObservableService } from './components/positions-table-multiple-review/reviewInputPositionObservableService';
import { ActivityReportEffects } from './effects/activity-report.effects';
import { WireIrsTaxDetailsComponent } from './components/wire-irs-tax-details/wire-irs-tax-details.component';
import {
  AchRequestingAccountHolderComponent
} from "./components/ach-requesting-account-holder/ach-requesting-account-holder.component";
import { MaskAccountNumberPipe } from '../shared/pipes/mask-account-number.pipe';

const COMPONENTS = [
  InputPageComponent,
  ReviewPageComponent,
  ConfirmationPageComponent,
  AchSourceOfFundsComponent,
  AchTransferDirectionComponent,
  TrustIncomePrincipalComponent,
  TrustTaxCategoryComponent,
  TrustTaxCodeComponent,
  TrustCommentComponent,
  TrustPaidForComponent,
  WireReceivingAccountHolderComponent,
  WireIsoReceivingAccountHolderComponent,
  WireAccountNumberComponent,
  WireRecipientComponent,
  WireTransmissionNotesComponent,
  CheckExtraPromptsComponent,
  TrustDepositCodeComponent,
  TransactionDetailsCheckComponent,
  WireStandingAuthComponent,
  CheckPayeeNameComponent,
  CheckThirdPartyAddressFieldsComponent,
  CheckThirdPartyDomesticFieldsComponent,
  CheckDeliveryMethodComponent,
  CheckReasonComponent,
  AchContributionReasonComponent,
  WireCreditToInterimComponent,
  WireIsoFourStepComponent,
  CheckThirdPartyInternationalFieldsComponent,
  WireRequestingAccountHolderComponent,
  WireDateDropdownControlComponent,
  SubmitAndCreateOptionComponent,
  OptionsComponent,
  TemplatesOptionComponent,
  WireTemplatesComponent,
  SaveAsTemplateModalComponent,
  ItMultipleCashInputsComponent,
  ItMultipleTaxWithholdingsComponent,
  ItMultipleTrustFieldsComponent,
  MultipleToAccountComponent,
  WireRecipientInterimComponent,
  MasterStatusViewComponent,
  WireIrsTaxDetailsComponent,
  AchRequestingAccountHolderComponent
];

const SHARED = [
  CheckPayeeTypeComponent,
  CheckRecipientComponent,
  CheckPayeeNameComponent,
  CheckAddressDetailsComponent,
  CheckReasonComponent,
  WireIntermediaryIsoComponent,
  WireRecipientComponent,
  WireReceivingAccountHolderComponent,
  WireIsoReceivingAccountHolderComponent,
  WireTransmissionNotesComponent,
  CheckMemoComponent,
  WireAbaComponent,
  CheckModifiedPayeeComponent,
  WireIrsTaxDetailsComponent
];

const CONTAINERS = [
  CreateRequestComponent,
  TransactionDetailsItComponent,
  MultipleTransactionDetailsItComponent,
  ItExtraPromptsComponent,
  TransactionDetailsAchComponent,
  AchExtraPromptsComponent,
  TransactionFormComponent,
  TransactionsTableComponent,
  PositionsLotsTableComponent,
  PositionsTableMultipleToAccountComponent,
  PositionsTableMultipleReviewComponent,
  TrustFieldsComponent,
  WireExtraPromptsComponent,
  TransactionDetailsWireInterimIsoComponent,
  RecurringTransactionsTableComponent,
  MasterTransactionsTableComponent
];

@NgModule({
  declarations: [COMPONENTS, SHARED, CONTAINERS, CheckModifiedPayeeComponent, WireModifiedPayeeComponent],
  imports: [
    CommonModule,
    SharedModule,
    AccountInfoModule,
    SwitchModule,
    TransactionViewModule,
    ReactiveFormsModule,
    AgGridModule,
    StoreModule.forFeature(
      fromCreateRequest.createRequestFeatureKey,
      fromCreateRequest.reducers
    ),
    EffectsModule.forFeature([
      CreateRequestEffects,
      InternalTransferEffects,
      InternalTransfersEffects,
      CashieringDateEffects,
      AchEffects,
      StandingAuthorizationsEffects,
      StandingAuthorizationsNicknameEffects,
      PtrsEffects,
      WireEffects,
      DraftsEffects,
      TransactionTableEffects,
      CheckEffects,
      LetterOfAuthorizationEffects,
      AuthorizeEffects,
      MasterTransactionTableEffects,
      MasterStatusViewEffects,
      ActivityReportEffects
    ]),
  ],
  providers: [TitleCasePipe, CurrencyPipe, DatePipe, DecimalPipe, BaseRestService, NamePipe, ReviewInputPositionObservableService, MaskAccountNumberPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  exports: [CreateRequestComponent, ReviewPageComponent, ConfirmationPageComponent, SHARED],
})
export class CreateRequestModule { }
